import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../App";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Progress } from "../../components/ui/progress";
import {
  LayoutDashboard,
  FileText,
  Clock,
  MessageSquare,
  LogOut,
  User,
  Building,
  Globe,
  Phone,
  Target,
  AlertCircle,
  MessageCircle,
  MapPin,
  Save,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const ClientProfile = () => {
  const { user, token, logout, API } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(1);
  const [profile, setProfile] = useState({
    company_name: "",
    industry: "",
    company_size: "",
    company_website: "",
    primary_contact_name: "",
    primary_contact_phone: "",
    goals: "",
    challenges: "",
    preferred_communication: "",
    client_timezone: "",
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${API}/client/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setProfile({
          company_name: data.company_name || "",
          industry: data.industry || "",
          company_size: data.company_size || "",
          company_website: data.company_website || "",
          primary_contact_name: data.primary_contact_name || data.user_name || "",
          primary_contact_phone: data.primary_contact_phone || "",
          goals: data.goals || "",
          challenges: data.challenges || "",
          preferred_communication: data.preferred_communication || "",
          client_timezone: data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
      }
    } catch (error) {
      console.error("Request failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const params = new URLSearchParams();
      Object.entries(profile).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await fetch(`${API}/client/profile?${params.toString()}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success("Profile saved successfully!");
        if (step < 3) {
          setStep(step + 1);
        } else {
          navigate("/portal");
        }
      } else {
        throw new Error("Failed to save profile");
      }
    } catch (error) {
      toast.error("Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const navItems = [
    { name: "Dashboard", path: "/portal", icon: LayoutDashboard },
    { name: "Invoices", path: "/portal/invoices", icon: FileText },
    { name: "Chat", path: "/portal/chat", icon: MessageSquare },
    { name: "Profile", path: "/portal/profile", icon: User, active: true },
  ];

  const industries = [
    "Healthcare Practice",
    "Home Health Agency",
    "Hospital System",
    "Long-term Care",
    "Mental Health",
    "Dental Practice",
    "Physical Therapy",
    "Medical Lab",
    "Healthcare Startup",
    "Life Sciences",
    "Other Healthcare",
  ];

  const companySizes = [
    { value: "1-10", label: "1-10 employees" },
    { value: "11-25", label: "11-25 employees" },
    { value: "26-50", label: "26-50 employees" },
    { value: "51-100", label: "51-100 employees" },
    { value: "101-250", label: "101-250 employees" },
    { value: "250+", label: "250+ employees" },
  ];

  const communicationPreferences = [
    { value: "email", label: "Email" },
    { value: "phone", label: "Phone Call" },
    { value: "video", label: "Video Call" },
    { value: "chat", label: "Portal Chat" },
  ];

  const timezones = [
    { value: "America/New_York", label: "Eastern Time (ET)" },
    { value: "America/Chicago", label: "Central Time (CT)" },
    { value: "America/Denver", label: "Mountain Time (MT)" },
    { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
    { value: "America/Anchorage", label: "Alaska Time (AKT)" },
    { value: "Pacific/Honolulu", label: "Hawaii Time (HT)" },
  ];

  const progressPercent = (step / 3) * 100;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-rnb-blue rounded-lg flex items-center justify-center">
              <span className="text-white font-heading font-bold text-lg">R</span>
            </div>
            <span className="font-heading font-semibold text-lg text-rnb-navy">
              Client Portal
            </span>
          </Link>

          <div className="flex items-center gap-4">
            <span className="text-sm text-rnb-gray">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={logout}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-white border-r border-slate-200 min-h-[calc(100vh-73px)] p-4">
          <nav className="space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  item.active
                    ? "bg-rnb-light text-rnb-blue"
                    : "text-rnb-gray hover:bg-slate-50"
                }`}
              >
                <item.icon className="h-5 w-5" />
                <span className="font-medium">{item.name}</span>
              </Link>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8">
          <div className="max-w-2xl mx-auto">
            <div className="mb-8">
              <h1 className="font-heading text-3xl font-bold text-rnb-navy mb-2">
                Company Profile
              </h1>
              <p className="text-rnb-gray">
                Help us understand your organization so we can serve you better.
              </p>
            </div>

            {/* Progress Indicator */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-rnb-navy">Step {step} of 3</span>
                <span className="text-sm text-rnb-gray">{Math.round(progressPercent)}% complete</span>
              </div>
              <Progress value={progressPercent} className="h-2" />
              <div className="flex justify-between mt-2">
                <span className={`text-xs ${step >= 1 ? "text-rnb-blue" : "text-rnb-gray"}`}>Company Info</span>
                <span className={`text-xs ${step >= 2 ? "text-rnb-blue" : "text-rnb-gray"}`}>Contact Details</span>
                <span className={`text-xs ${step >= 3 ? "text-rnb-blue" : "text-rnb-gray"}`}>Goals & Preferences</span>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
              </div>
            ) : (
              <Card data-testid="profile-form">
                <CardContent className="p-6">
                  {/* Step 1: Company Info */}
                  {step === 1 && (
                    <div className="space-y-6">
                      <div className="flex items-center gap-2 mb-4">
                        <Building className="h-5 w-5 text-rnb-blue" />
                        <h2 className="font-heading text-xl font-semibold text-rnb-navy">Company Information</h2>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="company_name">Company Name *</Label>
                        <Input
                          id="company_name"
                          value={profile.company_name}
                          onChange={(e) => handleChange("company_name", e.target.value)}
                          placeholder="Your company name"
                          data-testid="company-name-input"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="industry">Industry *</Label>
                        <Select value={profile.industry} onValueChange={(v) => handleChange("industry", v)}>
                          <SelectTrigger data-testid="industry-select">
                            <SelectValue placeholder="Select your industry" />
                          </SelectTrigger>
                          <SelectContent>
                            {industries.map((ind) => (
                              <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="company_size">Company Size *</Label>
                        <Select value={profile.company_size} onValueChange={(v) => handleChange("company_size", v)}>
                          <SelectTrigger data-testid="company-size-select">
                            <SelectValue placeholder="Select company size" />
                          </SelectTrigger>
                          <SelectContent>
                            {companySizes.map((size) => (
                              <SelectItem key={size.value} value={size.value}>{size.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="company_website">Company Website</Label>
                        <div className="flex">
                          <span className="flex items-center px-3 bg-slate-100 border border-r-0 rounded-l-md text-rnb-gray text-sm">
                            <Globe className="h-4 w-4" />
                          </span>
                          <Input
                            id="company_website"
                            value={profile.company_website}
                            onChange={(e) => handleChange("company_website", e.target.value)}
                            placeholder="www.yourcompany.com"
                            className="rounded-l-none"
                            data-testid="company-website-input"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 2: Contact Details */}
                  {step === 2 && (
                    <div className="space-y-6">
                      <div className="flex items-center gap-2 mb-4">
                        <User className="h-5 w-5 text-rnb-blue" />
                        <h2 className="font-heading text-xl font-semibold text-rnb-navy">Contact Details</h2>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="primary_contact_name">Primary Contact Name *</Label>
                        <Input
                          id="primary_contact_name"
                          value={profile.primary_contact_name}
                          onChange={(e) => handleChange("primary_contact_name", e.target.value)}
                          placeholder="Full name"
                          data-testid="contact-name-input"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="primary_contact_phone">Phone Number</Label>
                        <div className="flex">
                          <span className="flex items-center px-3 bg-slate-100 border border-r-0 rounded-l-md text-rnb-gray text-sm">
                            <Phone className="h-4 w-4" />
                          </span>
                          <Input
                            id="primary_contact_phone"
                            value={profile.primary_contact_phone}
                            onChange={(e) => handleChange("primary_contact_phone", e.target.value)}
                            placeholder="(555) 123-4567"
                            className="rounded-l-none"
                            data-testid="contact-phone-input"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="preferred_communication">Preferred Communication Method</Label>
                        <Select value={profile.preferred_communication} onValueChange={(v) => handleChange("preferred_communication", v)}>
                          <SelectTrigger data-testid="communication-select">
                            <SelectValue placeholder="How would you like us to contact you?" />
                          </SelectTrigger>
                          <SelectContent>
                            {communicationPreferences.map((pref) => (
                              <SelectItem key={pref.value} value={pref.value}>{pref.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="client_timezone">Your Timezone</Label>
                        <Select value={profile.client_timezone} onValueChange={(v) => handleChange("client_timezone", v)}>
                          <SelectTrigger data-testid="timezone-select">
                            <SelectValue placeholder="Select your timezone" />
                          </SelectTrigger>
                          <SelectContent>
                            {timezones.map((tz) => (
                              <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  {/* Step 3: Goals & Preferences */}
                  {step === 3 && (
                    <div className="space-y-6">
                      <div className="flex items-center gap-2 mb-4">
                        <Target className="h-5 w-5 text-rnb-blue" />
                        <h2 className="font-heading text-xl font-semibold text-rnb-navy">Goals & Challenges</h2>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="goals">What are your main goals for working with us?</Label>
                        <Textarea
                          id="goals"
                          value={profile.goals}
                          onChange={(e) => handleChange("goals", e.target.value)}
                          placeholder="e.g., Improve compliance, streamline operations, staff training..."
                          rows={4}
                          data-testid="goals-textarea"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="challenges">What challenges are you currently facing?</Label>
                        <Textarea
                          id="challenges"
                          value={profile.challenges}
                          onChange={(e) => handleChange("challenges", e.target.value)}
                          placeholder="e.g., Regulatory audits, staff turnover, operational inefficiencies..."
                          rows={4}
                          data-testid="challenges-textarea"
                        />
                      </div>

                      <div className="bg-green-50 border border-green-100 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                          <div>
                            <p className="font-medium text-green-800">You&apos;re all set!</p>
                            <p className="text-sm text-green-700">
                              Once you submit, your consultant will review your profile and reach out to schedule your kickoff call.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Navigation Buttons */}
                  <div className="flex items-center justify-between mt-8 pt-6 border-t">
                    <Button
                      variant="ghost"
                      onClick={() => step > 1 ? setStep(step - 1) : navigate("/portal")}
                      data-testid="back-btn"
                    >
                      {step > 1 ? "Back" : "Skip for now"}
                    </Button>

                    <Button onClick={handleSave} disabled={saving} data-testid="next-btn">
                      {saving ? "Saving..." : step < 3 ? (
                        <>
                          Continue <ArrowRight className="h-4 w-4 ml-2" />
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" /> Complete Profile
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ClientProfile;
